// САНДБОКС: dev-полигон для проверки логики карт.
// НЕ в основном меню (только по URL /sandbox).
//
// Что умеет:
//   1. Выбор Героев обеих сторон перед матчем.
//   2. Контроль обеих сторон — toggle [A / B].
//   3. Библиотека всех карт фракции активной стороны — тап = добавить
//      в руку (для тестов конкретных комбо).
//   4. Стандартные механики из BattleScreen (drag-n-drop, АТАКА, Стенд,
//      пенды, end-turn) — но без бота.
//   5. Skip мулигана, skip Action initiation warning (но всё равно
//      движок сожжёт rare на ходу 1 — это правило боя).
//
// Reuse: компоненты из BattleScreen не экспортированы (defined inside),
// поэтому Sandbox пишет свои упрощённые версии. Долгосрочно — вынести
// в shared components/battle/*.

import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PHASE, createSandboxMatch, addCardToHand, removeCardFromHand,
  setPendingPlacement, cancelPendingPlacement, canAddPlacement, setPendingOrderOpts,
  commitPlacementsAndReveal, heroAttackOrSkipAnimated, canHeroAttackHero, canHeroAttackUnit,
  setPlayerPendingAction, clearPlayerPendingAction,
  actionNeedsTarget, getActionTargets,
  getHeroActiveInfo, useHeroActive, getHeroActiveTargets, getHeroActiveDiscardChoices,
  type Match
} from '../engine-bridge';
import { HeroAvatar } from '../components/HeroAvatar';
import { GameCard } from '../components/GameCard';
import { getCardData, type CardData } from '../lib/cardData';
import { Animator } from '../animation/Animator';
import { AnimationLayer } from '../animation/AnimationLayer';
// Импорт всех данных карт + Героев напрямую — sandbox видит ВСЁ.
// @ts-ignore
import { cards as cardsDeus } from '@data/cards-deus.js';
// @ts-ignore
import { cards as cardsSyn } from '@data/cards-syntetics.js';
// @ts-ignore
import { cards as cardsGlb } from '@data/cards-global.js';
// @ts-ignore
import { cards as cardsLiv } from '@data/cards-livlife.js';
// @ts-ignore
import { cards as cardsNeut } from '@data/cards-neutral.js';
// @ts-ignore
import { heroes as heroesDeus } from '@data/heroes-deus.js';
// @ts-ignore
import { heroes as heroesSyn } from '@data/heroes-syntetics.js';
// @ts-ignore
import { heroes as heroesGlb } from '@data/heroes-global.js';
// @ts-ignore
import { heroes as heroesLiv } from '@data/heroes-livlife.js';
// @ts-ignore
import { heroes as heroesNeut } from '@data/heroes-neutral.js';
import styles from './SandboxScreen.module.css';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';
const FACTION_LABEL: Record<Faction, string> = {
  deus: 'Деус Системс',
  syntetics: 'Синтетикс',
  global: 'Глобал Секьюрити',
  livlife: 'Лив Лайф',
  neutral: 'Нейтрал/Улица'
};

const ALL_FACTIONS: Faction[] = ['deus', 'syntetics', 'global', 'livlife', 'neutral'];

const HEROES_BY_FACTION: Record<Faction, any[]> = {
  deus: heroesDeus,
  syntetics: heroesSyn,
  global: heroesGlb,
  livlife: heroesLiv,
  neutral: heroesNeut,
};
const CARDS_BY_FACTION: Record<Faction, any[]> = {
  deus: cardsDeus,
  syntetics: cardsSyn,
  global: cardsGlb,
  livlife: cardsLiv,
  neutral: cardsNeut,
};

function factionShort(f: Faction): string {
  return f === 'syntetics' ? 'syn'
       : f === 'global' ? 'gsec'
       : f === 'livlife' ? 'liv'
       : f === 'neutral' ? 'neut'
       : 'deus';
}
function phaseLabel(p: string): string {
  switch (p) {
    case PHASE.MULLIGAN: return 'мулиган';
    case PHASE.DRAW: return 'тяга';
    case PHASE.PLACEMENT: return 'размещение';
    case PHASE.REVEAL: return 'раскрытие';
    case PHASE.HERO_FIRST: return 'герой 1';
    case PHASE.HERO_SECOND: return 'герой 2';
    case PHASE.END_TURN: return 'конец хода';
    case PHASE.GAME_OVER: return 'окончен';
    default: return p;
  }
}

// ────────── Pre-game setup ──────────
function SandboxSetup({ onStart }: {
  onStart: (factionA: Faction, heroA: string, factionB: Faction, heroB: string, noInitiation: boolean) => void;
}) {
  const [factionA, setFactionA] = useState<Faction>('deus');
  const [heroA, setHeroA] = useState<string>(heroesDeus[0]?.id || '');
  const [factionB, setFactionB] = useState<Faction>('syntetics');
  const [heroB, setHeroB] = useState<string>(heroesSyn[0]?.id || '');
  // САНД.1: режим без инициации — эпики/леги активны сразу, Действия не
  // сгорают раньше срока. По умолчанию ВКЛ (для теста удобнее).
  const [noInitiation, setNoInitiation] = useState(true);

  const heroesA = HEROES_BY_FACTION[factionA] || [];
  const heroesB = HEROES_BY_FACTION[factionB] || [];

  const onFactionAChange = (f: Faction) => {
    setFactionA(f);
    const list = HEROES_BY_FACTION[f] || [];
    if (list.length > 0) setHeroA(list[0].id);
  };
  const onFactionBChange = (f: Faction) => {
    setFactionB(f);
    const list = HEROES_BY_FACTION[f] || [];
    if (list.length > 0) setHeroB(list[0].id);
  };

  return (
    <div className={styles.setupRoot}>
      <div className={styles.setupBox}>
        <div className={styles.setupTitle}>САНДБОКС</div>
        <div className={styles.setupSub}>
          Полигон для тестов. Контроль обеих сторон, библиотека всех карт.
        </div>

        <div className={styles.setupSection}>
          <div className={styles.setupSideLabel}>СТОРОНА A</div>
          <div className={styles.setupRow}>
            <label className={styles.setupLbl}>Фракция</label>
            <select
              className={styles.setupSelect}
              value={factionA}
              onChange={(e) => onFactionAChange(e.target.value as Faction)}
            >
              {ALL_FACTIONS.map(f => (
                <option key={f} value={f}>{FACTION_LABEL[f]}</option>
              ))}
            </select>
          </div>
          <div className={styles.setupRow}>
            <label className={styles.setupLbl}>Герой</label>
            <select
              className={styles.setupSelect}
              value={heroA}
              onChange={(e) => setHeroA(e.target.value)}
            >
              {heroesA.map((h: any) => (
                <option key={h.id} value={h.id}>
                  {h.name_ru} ({h.atk}/{h.armor}/{h.hp})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.setupSection}>
          <div className={styles.setupSideLabel}>СТОРОНА B</div>
          <div className={styles.setupRow}>
            <label className={styles.setupLbl}>Фракция</label>
            <select
              className={styles.setupSelect}
              value={factionB}
              onChange={(e) => onFactionBChange(e.target.value as Faction)}
            >
              {ALL_FACTIONS.map(f => (
                <option key={f} value={f}>{FACTION_LABEL[f]}</option>
              ))}
            </select>
          </div>
          <div className={styles.setupRow}>
            <label className={styles.setupLbl}>Герой</label>
            <select
              className={styles.setupSelect}
              value={heroB}
              onChange={(e) => setHeroB(e.target.value)}
            >
              {heroesB.map((h: any) => (
                <option key={h.id} value={h.id}>
                  {h.name_ru} ({h.atk}/{h.armor}/{h.hp})
                </option>
              ))}
            </select>
          </div>
        </div>

        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            padding: '10px 4px', fontSize: 13, color: 'var(--t-hi, #fff)'
          }}
        >
          <input
            type="checkbox"
            checked={noInitiation}
            onChange={(e) => setNoInitiation(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--neon-cy, #3DF0FF)' }}
          />
          Без инициации (эпики/леги активны сразу, Действия не сгорают)
        </label>

        <button
          className={styles.setupStartBtn}
          onClick={() => onStart(factionA, heroA, factionB, heroB, noInitiation)}
          disabled={!heroA || !heroB}
        >
          ► НАЧАТЬ ТЕСТ
        </button>
      </div>
    </div>
  );
}

// ────────── Мини-юнит на клетке ──────────
function UnitMini({ unit, faction }: { unit: any; faction: Faction }) {
  const data = getCardData(unit.id);
  const atk = unit.current?.atk ?? unit.base?.atk ?? data?.atk ?? 0;
  const armor = unit.current?.armor ?? unit.base?.armor ?? data?.armor ?? 0;
  const hp = unit.current?.hp ?? unit.base?.hp ?? data?.hp ?? 0;
  const maxHp = unit.base?.hp ?? data?.hp ?? hp;
  const hpRatio = maxHp > 0 ? hp / maxHp : 1;
  const fShort = factionShort(faction);
  const rarity = unit.rarity || data?.rarity || 'common';
  const name = unit.name_ru || data?.name_ru || unit.id;
  const kw: string[] = unit.current?.keywords || unit.keywords || [];
  const taunt = kw.includes('taunt');
  const silenced = !!unit.current?.silenced || !!(unit.silenceSources && unit.silenceSources.size > 0);

  return (
    <div className={`${styles.unit} ${styles[`f-${fShort}`]} ${styles[`r-${rarity}`]}`}>
      <div className={styles.uArt}>
        <img
          src={`/img/cards/${unit.id}.jpg`}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          loading="eager"
        />
      </div>
      <div className={styles.uName}>{name}</div>
      {(taunt || silenced) && (
        <div className={styles.uBadges}>
          {taunt && <span className={`${styles.uBadge} ${styles.uBadgeTaunt}`}>🛡</span>}
          {silenced && <span className={`${styles.uBadge} ${styles.uBadgeSilence}`}>🔇</span>}
        </div>
      )}
      <div className={styles.uHpBar}>
        <div
          className={styles.uHpFill}
          style={{
            width: `${hpRatio * 100}%`,
            background: hpRatio < 0.25 ? 'var(--neon-rd)' : hpRatio < 0.5 ? 'var(--neon-am)' : 'var(--neon-li)'
          }}
        />
      </div>
      <div className={styles.uStats}>
        <span className={styles.uStatAtk}>{atk}</span>
        <span className={styles.uStatArm}>{armor}</span>
        <span className={styles.uStatHp}>{hp}</span>
      </div>
    </div>
  );
}

// ────────── Главный sandbox-компонент ──────────
export function SandboxScreen() {
  const navigate = useNavigate();

  // Match создаётся после setup.
  const [match, setMatch] = useState<Match | null>(null);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const refresh = forceUpdate;

  // Какую сторону контролирует юзер: 0 = A (низ экрана), 1 = B (верх).
  // Toggle button переключает.
  const [activeSide, setActiveSide] = useState<0 | 1>(0);
  // Какая сторона визуально снизу (та, которую контролируешь).
  // Удобнее: твоя сторона всегда снизу, переключаешь = пересобираешь UI.
  const myIdx = activeSide;
  const enemyIdx: 0 | 1 = (1 - activeSide) as 0 | 1;

  // UI state
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Авто-открывает библиотеку как боковую панель на широких экранах (>=1100px,
  // нужна ширина под полноразмерные карточки 320px + поле 520px + отступы).
  const [isWideScreen, setIsWideScreen] = useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches
  );
  // Ultra-wide (≥1500px): помещаются ОБЕ библиотеки — слева A, справа B.
  const [isUltraWide, setIsUltraWide] = useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1500px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1100px)');
    const onChange = () => setIsWideScreen(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else (mq as any).addListener(onChange); // fallback Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else (mq as any).removeListener(onChange);
    };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1500px)');
    const onChange = () => setIsUltraWide(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else (mq as any).addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else (mq as any).removeListener(onChange);
    };
  }, []);
  // Sandbox — dev-полигон: разворачиваем приложение во весь экран браузера.
  // В web-режиме контент обычно зажат в «телефонную» рамку #root 390×844,
  // из-за чего широкая раскладка поля ломалась. Класс снимается при выходе.
  useEffect(() => {
    document.body.classList.add('sandbox-fullscreen');
    return () => document.body.classList.remove('sandbox-fullscreen');
  }, []);
  const [detailCard, setDetailCard] = useState<CardData | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const [heroAttackArmed, setHeroAttackArmed] = useState(false);
  const [actionPick, setActionPick] = useState<{ card: any; targetIds: Set<number> } | null>(null);
  // САНД.2: move-Действия (Перегруппировка/Передислокация) требуют 2-й
  // выбор — куда переместить юнита. Без него движок вернёт no_destination
  // и эффект «не отработает» (баг был именно в sandbox, не в карте).
  const [actionMovePick, setActionMovePick] = useState<{ card: any; target: any } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // ВАЖНО: ВСЕ хуки ДО early return! Иначе React throws «more hooks than
  // previous render» когда match переходит из null в Match.
  const [selectedHandCard, setSelectedHandCard] = useState<any | null>(null);
  // Активки Героя — target-picker. heroActivePick: выбор юнита(ов) на поле
  // ('unit' — одна цель; 'unit_slot' — Кироса: юнит + своя ячейка).
  // heroDiscardPick: список юнитов из сброса (Лиза). heroActiveMsg —
  // транзиентный тост-результат применения активки.
  const [heroActivePick, setHeroActivePick] = useState<{
    kind: 'unit' | 'unit_slot' | 'unit_unit';
    stage: 'unit' | 'slot' | 'unit2';
    targetIds: Set<number>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pickedCard?: any;
  } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [heroDiscardPick, setHeroDiscardPick] = useState<any[] | null>(null);
  const [heroActiveMsg, setHeroActiveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Animator
  const animatorRef = useRef<Animator>(new Animator());
  if (match) animatorRef.current.rerenderFn = forceUpdate;

  // Init match.
  const startMatch = (factionA: Faction, heroA: string, factionB: Faction, heroB: string, noInitiation: boolean) => {
    const m = createSandboxMatch(factionA, heroA, factionB, heroB, noInitiation);
    setMatch(m);
  };

  // ESC handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (libraryOpen) setLibraryOpen(false);
      else if (detailCard) setDetailCard(null);
      else if (heroDiscardPick) setHeroDiscardPick(null);
      else if (heroActivePick) setHeroActivePick(null);
      else if (actionMovePick) setActionMovePick(null);
      else if (actionPick) setActionPick(null);
      else if (heroAttackArmed) setHeroAttackArmed(false);
      else if (logOpen) setLogOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [libraryOpen, detailCard, heroDiscardPick, heroActivePick, actionPick, actionMovePick, heroAttackArmed, logOpen]);

  // САНД.3: dev-API для автоматизированного прогона карт из JS-консоли.
  // Sandbox без анимаций → можно программно строить ситуации и читать
  // результаты через ТОТ ЖЕ движок что в бою (ловит интеграционные баги,
  // которые unit-тесты не видят). window.__sb доступен только в /sandbox.
  useEffect(() => {
    if (!match) return;
    const ALL = [...cardsDeus, ...cardsSyn, ...cardsGlb, ...cardsLiv, ...cardsNeut];
    const dumpSide = (side: 0 | 1) => {
      const p = match.players[side];
      const units: any[] = [];
      (p.field || []).forEach((row: any[], li: number) => row.forEach((u: any, si: number) => {
        if (u) units.push({
          id: u.id, name: u.name_ru, line: li, slot: si,
          atk: u.current?.atk, armor: u.current?.armor, hp: u.current?.hp,
          baseHp: u.base?.hp,
          infected: u.infectedStacks || 0,
          mods: (u.modifications || []).length,
          keywords: u.current?.keywords || u.keywords || [],
          sleeping: typeof u.isSleeping === 'function' ? u.isSleeping(match.currentTurn) : null
        });
      }));
      return { hero: { hp: p.hero?.current?.hp ?? p.hero?.hp, atk: p.hero?.current?.atk }, units };
    };
    (window as any).__sb = {
      get match() { return match; },
      refresh,
      PHASE,
      turn: () => match.currentTurn,
      phase: () => match.phase,
      log: () => (match.log?.entries || []).slice(-30),
      find: (id: string) => ALL.find(c => c.id === id),
      dump: (side: 0 | 1) => dumpSide(side),
      both: () => ({ A: dumpSide(0), B: dumpSide(1), turn: match.currentTurn, phase: match.phase }),
      // Найти Card-instance по instanceId (в hand/pendingPlacements/field обеих сторон).
      findInstance: (iid: number) => {
        for (const p of match.players) {
          for (const c of (p.hand || [])) if (c?.instanceId === iid) return c;
          for (const row of (p.field || [])) for (const u of (row || [])) if (u?.instanceId === iid) return u;
        }
        for (const pp of (match.pendingPlacements || [])) for (const c of (pp || [])) if (c?.instanceId === iid) return c;
        return null;
      },
      // Хелпер: поставить юнит/мод в pending. unit → line/slot; mod → targetInstanceId.
      // orderOpts — для юнитов-приказов с выбором (напр. Зеро: {chosenLine:0}).
      place: (side: 0 | 1, cardId: string, line?: 0 | 1, slot?: number, targetInstanceId?: number, orderOpts?: any) => {
        const tmpl = ALL.find(c => c.id === cardId);
        if (!tmpl) return 'NO_CARD:' + cardId;
        addCardToHand(match, side, tmpl);
        const hand = match.players[side].hand;
        const card = hand[hand.length - 1];
        const payload: any = { cardInstanceId: card.instanceId };
        if (targetInstanceId != null) payload.targetInstanceId = targetInstanceId;
        else { payload.line = line; payload.slot = slot; }
        setPendingPlacement(match, side, payload);
        if (orderOpts) {
          // Если orderOpts.target — число (instanceId), резолвим в Card-объект
          // (order-юниты типа Оникса ждут opts.target как объект, не id).
          const oo = { ...orderOpts };
          if (typeof oo.target === 'number') {
            for (const p of match.players) {
              for (const c of (p.hand || [])) if (c?.instanceId === oo.target) oo.target = c;
              for (const row of (p.field || [])) for (const u of (row || [])) if (u?.instanceId === oo.target) oo.target = u;
            }
            for (const pp of (match.pendingPlacements || [])) for (const c of (pp || [])) if (c?.instanceId === oo.target) oo.target = c;
          }
          setPendingOrderOpts(match, side, card.instanceId, oo);
        }
        return card.instanceId;
      },
      // Хелпер: разыграть Действие. opts напр. {destLine,destSlot} для move.
      playAction: (side: 0 | 1, cardId: string, targetInstanceId?: number | null, opts: any = {}) => {
        const tmpl = ALL.find(c => c.id === cardId);
        if (!tmpl) return 'NO_CARD:' + cardId;
        addCardToHand(match, side, tmpl);
        const hand = match.players[side].hand;
        const card = hand[hand.length - 1];
        setPlayerPendingAction(match, side, card.instanceId, targetInstanceId ?? null, opts);
        return card.instanceId;
      },
      // Завершить размещение + reveal (Ф3) — как кнопка ХОД.
      endTurn: async () => { await commitPlacementsAndReveal(match, animatorRef.current); refresh(); },
      // Пропустить фазу Героя.
      passHero: async () => { await heroAttackOrSkipAnimated(match, null, animatorRef.current); refresh(); },
      // САНД.4: пересоздать матч из JS (без возврата на setup-экран).
      // После вызова нужен короткий wait — __sb переопределится новым match.
      // heroA/heroB — id Героя; по умолчанию первый Герой фракции.
      reset: (factionA = 'global', heroA?: string, factionB = 'livlife', heroB?: string, noInit = true) => {
        const HERO0: Record<string, string> = {
          global: 'global_iv', livlife: 'livlife_ariana',
          deus: 'deus_kirosa', syntetics: 'syn_bishor', neutral: 'neutral_lider_ulicy'
        };
        const m = createSandboxMatch(
          factionA as any, heroA || HERO0[factionA],
          factionB as any, heroB || HERO0[factionB],
          noInit
        );
        setMatch(m);
        return 'reset:ok (wait ~300ms перед использованием __sb)';
      },
      bridge: {
        addCardToHand, setPendingPlacement, cancelPendingPlacement, canAddPlacement,
        commitPlacementsAndReveal, setPlayerPendingAction, clearPlayerPendingAction,
        getActionTargets, actionNeedsTarget,
        getHeroActiveInfo, useHeroActive, getHeroActiveTargets,
        heroAttackOrSkipAnimated, animator: animatorRef.current
      }
    };
  }, [match, refresh]);

  if (!match) {
    return <SandboxSetup onStart={startMatch} />;
  }

  const me = match.players[myIdx];
  const enemy = match.players[enemyIdx];
  const myFaction = me.hero.faction as Faction;
  const enemyFaction = enemy.hero.faction as Faction;
  const turn = match.currentTurn;
  const phase = phaseLabel(match.phase);
  const isPlacement = match.phase === PHASE.PLACEMENT;
  const isHeroPhase = match.phase === PHASE.HERO_FIRST || match.phase === PHASE.HERO_SECOND;

  // pending placement map (для отрисовки ghost'ов на клетках).
  const pendingMap = new Map<string, any>();
  for (const pl of (match.pendingPlacements?.[myIdx] || [])) {
    const card = me.hand.find((c: any) => c.instanceId === pl.cardInstanceId);
    if (!card) continue;
    if (pl.line != null && pl.slot != null) {
      pendingMap.set(`${pl.line}-${pl.slot}`, card);
    }
  }
  const pendingActionId: number | null = me.pendingAction?.cardInstanceId ?? null;

  // Тап на карту в руке.
  const onHandCardTap = (card: any) => {
    if (!isPlacement) return;
    // Если это карта-Действие → открыть детали с возможностью сыграть.
    if (card.type === 'action') {
      const data = getCardData(card.id);
      if (data) setDetailCard(data);
      return;
    }
    // Если pending — отменить.
    const isPending = (match.pendingPlacements?.[myIdx] || []).some(
      (p: any) => p.cardInstanceId === card.instanceId
    );
    if (isPending) {
      cancelPendingPlacement(match, myIdx, card.instanceId);
      refresh();
      return;
    }
    // Иначе — открыть детали (плейсмент Юнита через тап на ячейку ниже).
    const data = getCardData(card.id);
    if (data) setDetailCard(data);
  };

  // (selectedHandCard объявлен выше — ДО early return.)

  const onHandCardSelect = (card: any) => {
    if (!isPlacement) return;
    if (card.type === 'action') {
      // Action → детали (с СЫГРАТЬ кнопкой).
      const data = getCardData(card.id);
      if (data) setDetailCard(data);
      return;
    }
    const isPending = (match.pendingPlacements?.[myIdx] || []).some(
      (p: any) => p.cardInstanceId === card.instanceId
    );
    if (isPending) {
      cancelPendingPlacement(match, myIdx, card.instanceId);
      refresh();
      return;
    }
    setSelectedHandCard(card);
  };

  const onCellTap = async (line: 0 | 1, slot: number, isEnemySide: boolean) => {
    // HERO ACTIVE PICK: выбор цели для активки Героя.
    if (heroActivePick) {
      if (heroActivePick.stage === 'unit') {
        const u = (isEnemySide ? enemy : me).field[line][slot];
        if (!u || !heroActivePick.targetIds.has(u.instanceId)) return;
        if (heroActivePick.kind === 'unit') {
          const r = useHeroActive(match, myIdx, u);
          setHeroActivePick(null);
          showHeroActiveResult(r);
          if (r.ok) refresh();
        } else if (heroActivePick.kind === 'unit_slot') {
          // Кироса: вражеский юнит выбран → выбираем свою пустую ячейку.
          setHeroActivePick({ ...heroActivePick, stage: 'slot', pickedCard: u });
        } else {
          // Раст: юнит-источник выбран → выбираем приёмник (другой свой юнит).
          const destIds = new Set<number>();
          for (const ln of [0, 1]) {
            for (const cell of me.field[ln]) {
              if (cell && cell.instanceId !== u.instanceId) destIds.add(cell.instanceId);
            }
          }
          if (destIds.size === 0) {
            setHeroActivePick(null);
            showHeroActiveResult({ ok: false, reason: 'нет второго юнита для приёма Мода' });
          } else {
            setHeroActivePick({ kind: 'unit_unit', stage: 'unit2', targetIds: destIds, pickedCard: u });
          }
        }
        return;
      }
      if (heroActivePick.stage === 'unit2') {
        // Раст: выбор юнита-приёмника Мода.
        const u = (isEnemySide ? enemy : me).field[line][slot];
        if (!u || !heroActivePick.targetIds.has(u.instanceId)) return;
        const r = useHeroActive(match, myIdx, { source: heroActivePick.pickedCard, dest: u });
        setHeroActivePick(null);
        showHeroActiveResult(r);
        if (r.ok) refresh();
        return;
      }
      // stage 'slot' (Кироса) — тап по своей пустой ячейке.
      if (isEnemySide || me.field[line][slot] != null) return;
      const r = useHeroActive(match, myIdx, { card: heroActivePick.pickedCard, line, slot });
      setHeroActivePick(null);
      showHeroActiveResult(r);
      if (r.ok) refresh();
      return;
    }
    // ATTACK MODE: тап по вражеской клетке = атака Героем.
    if (heroAttackArmed && isEnemySide) {
      const unit = enemy.field[line][slot];
      if (!unit) return;
      if (!canHeroAttackUnit(match, myIdx, unit)) return;
      setHeroAttackArmed(false);
      // БОЙ.1.9: анимация атаки Героя.
      await heroAttackOrSkipAnimated(match, unit, animatorRef.current);
      refresh();
      return;
    }
    // ACTION PICK MODE: тап по любой подсвеченной клетке = выбрать цель.
    if (actionPick) {
      const u = (isEnemySide ? enemy : me).field[line][slot];
      if (!u) return;
      if (!actionPick.targetIds.has(u.instanceId)) return;
      // САНД.2: move-Действия (Перегруппировка/Передислокация) — после выбора
      // юнита нужен 2-й тап: КУДА переместить. Переходим в режим actionMovePick.
      const MOVE_ACTIONS = ['global_act_peregruppirovka', 'global_act_peredislokatsiya'];
      if (MOVE_ACTIONS.includes(actionPick.card.id)) {
        setActionMovePick({ card: actionPick.card, target: u });
        setActionPick(null);
        flashToast(true, 'Теперь тапни свободную клетку своей стороны — куда переместить');
        return;
      }
      setPlayerPendingAction(match, myIdx, actionPick.card.instanceId, u.instanceId, {});
      setActionPick(null);
      refresh();
      return;
    }
    // САНД.2: MOVE DESTINATION PICK — тап по свободной клетке своей стороны.
    if (actionMovePick) {
      if (isEnemySide) { flashToast(false, 'Перемещать можно только на свою сторону'); return; }
      const occupied = me.field[line][slot] || pendingMap.get(`${line}-${slot}`);
      if (occupied) { flashToast(false, 'Клетка занята — выбери свободную'); return; }
      setPlayerPendingAction(
        match, myIdx,
        actionMovePick.card.instanceId,
        actionMovePick.target.instanceId,
        { destLine: line, destSlot: slot }
      );
      setActionMovePick(null);
      refresh();
      return;
    }
    // ПОСТАНОВКА: тап по моей клетке + selectedHandCard.
    if (isPlacement && !isEnemySide && selectedHandCard) {
      // Мод вешается на своего юнита (на поле или запланированного в этот ход),
      // а не на пустую клетку.
      if (selectedHandCard.type === 'mod') {
        const target = me.field[line][slot] || pendingMap.get(`${line}-${slot}`);
        if (!target) {
          flashToast(false, 'Мод вешается на юнит — тапни своего юнита');
          return;
        }
        const okMod = canAddPlacement(match, myIdx, selectedHandCard);
        if (!okMod.ok) {
          flashToast(false, `Мод: ${denyReason(okMod.reason)}`);
          return;
        }
        setPendingPlacement(match, myIdx, {
          cardInstanceId: selectedHandCard.instanceId,
          targetInstanceId: target.instanceId,
        });
        setSelectedHandCard(null);
        flashToast(true, `◆ Мод нацелен на «${target.name_ru || target.id}» — применится на ▶ ХОД`);
        refresh();
        return;
      }
      const cell = me.field[line][slot];
      if (cell != null) return; // занято
      // Не ставим если уже pending на эту клетку.
      if (pendingMap.has(`${line}-${slot}`)) return;
      const ok = canAddPlacement(match, myIdx, selectedHandCard);
      if (!ok.ok) {
        flashToast(false, `Размещение: ${denyReason(ok.reason)}`);
        return;
      }
      setPendingPlacement(match, myIdx, {
        cardInstanceId: selectedHandCard.instanceId,
        line, slot
      });
      setSelectedHandCard(null);
      refresh();
      return;
    }
    // По юниту врага вне атаки = детали.
    const unit = (isEnemySide ? enemy : me).field[line][slot];
    if (unit) {
      const d = getCardData(unit.id);
      if (d) setDetailCard(d);
    }
  };

  const onEnemyHeroTap = async () => {
    if (heroAttackArmed && canHeroAttackHero(match, myIdx)) {
      setHeroAttackArmed(false);
      // БОЙ.1.9: анимация атаки Героя.
      await heroAttackOrSkipAnimated(match, enemy.hero, animatorRef.current);
      refresh();
      return;
    }
    // Иначе — открыть детали Героя.
    const heroData = enemy.hero;
    // Чтобы показать в CardDetailModal как карту — конструируем data.
    const data: CardData = {
      id: heroData.id,
      name_ru: heroData.name_ru,
      faction: heroData.faction,
      type: 'unit', // нет 'hero' типа в CardData
      rarity: heroData.rarity || 'special',
      atk: heroData.base?.atk ?? heroData.atk ?? 0,
      armor: heroData.base?.armor ?? heroData.armor ?? 0,
      hp: heroData.base?.hp ?? heroData.hp ?? 0,
      keywords: heroData.keywords || [],
      exclusive_hero: null,
      ability_text: heroData.ability_text || {},
      flavor_ru: heroData.flavor_ru || null,
    };
    setDetailCard(data);
  };

  // End turn — выполняет Ф3 (commit), потом проходит Ф4/Ф5 (атаки Героев
  // вручную). Поскольку нет бота, юзер сам должен переключать активную
  // сторону для атак во время Hero фаз.
  const onEndTurn = async () => {
    if (!isPlacement || isProcessing) return;
    setIsProcessing(true);
    try {
      await commitPlacementsAndReveal(match, animatorRef.current);
      refresh();
    } catch (e) {
      console.error('[sandbox] commit failed:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  // Pass hero attack (в HERO_FIRST/SECOND).
  const onPassHero = async () => {
    if (!isHeroPhase || isProcessing) return;
    setHeroAttackArmed(false);
    // null target — пропуск (без anim, фаза просто продвигается).
    await heroAttackOrSkipAnimated(match, null, animatorRef.current);
    refresh();
  };

  // Sandbox library: добавить карту в руку выбранной стороны.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onAddCardToHand = (sideIdx: 0 | 1, cardTemplate: any) => {
    const ok = addCardToHand(match, sideIdx, cardTemplate);
    if (ok) refresh();
  };

  // Действия: тап на карту в руке открывает детали с СЫГРАТЬ.
  // Если карта-Action — нажатие СЫГРАТЬ помещает в pendingAction.
  const onPlayAction = (card: any) => {
    if (!isPlacement) return;
    if (actionNeedsTarget(card.id)) {
      const targets = getActionTargets(match, myIdx, card.id);
      const targetIds = new Set<number>(
        targets.map((u: any) => u.instanceId).filter((id: any) => typeof id === 'number')
      );
      if (targetIds.size === 0) {
        alert('Нет валидных целей для этого Действия');
        return;
      }
      setActionPick({ card, targetIds });
      setDetailCard(null);
    } else {
      setPlayerPendingAction(match, myIdx, card.instanceId, null, {});
      setDetailCard(null);
      refresh();
    }
  };

  // ─── Транзиентный тост (зелёный/красный, авто-скрытие) ───
  // Используется и активками Героя, и фидбеком по размещению карт/Модов.
  const flashToast = (ok: boolean, text: string) => {
    setHeroActiveMsg({ ok, text });
    window.setTimeout(() => setHeroActiveMsg(null), 3500);
  };
  const showHeroActiveResult = (r: { ok: boolean; reason?: string }) => {
    flashToast(r.ok, r.ok ? '◆ Активка Героя применена' : `✗ Активка: ${r.reason || 'отказ'}`);
  };

  // Клик по кнопке активки. targetKind решает флоу: сразу / выбор юнита /
  // юнит+своя ячейка (Кироса) / выбор из сброса (Лиза).
  const onHeroActiveClick = () => {
    const info = getHeroActiveInfo(match, myIdx);
    if (!info.exists) {
      showHeroActiveResult({ ok: false, reason: 'у этого Героя активка не подключена' });
      return;
    }
    if (!info.available) {
      showHeroActiveResult({ ok: false, reason: info.reason || 'недоступна' });
      return;
    }
    if (info.targetKind === 'none') {
      const r = useHeroActive(match, myIdx);
      showHeroActiveResult(r);
      if (r.ok) refresh();
      return;
    }
    if (info.targetKind === 'discard') {
      const choices = getHeroActiveDiscardChoices(match, myIdx);
      if (choices.length === 0) {
        showHeroActiveResult({ ok: false, reason: 'в сбросах нет юнитов' });
        return;
      }
      setActionPick(null);
      setSelectedHandCard(null);
      setHeroAttackArmed(false);
      setHeroDiscardPick(choices);
      return;
    }
    // 'unit' | 'unit_slot' | 'unit_unit' — выбор юнита(ов) на поле.
    const targets = getHeroActiveTargets(match, myIdx);
    const ids = new Set<number>(
      targets.map((u: any) => u?.instanceId).filter((x: any) => typeof x === 'number')
    );
    if (ids.size === 0) {
      showHeroActiveResult({ ok: false, reason: 'нет целей на поле' });
      return;
    }
    setActionPick(null);
    setSelectedHandCard(null);
    setHeroAttackArmed(false);
    setHeroActivePick({
      kind: info.targetKind === 'unit_slot' ? 'unit_slot'
        : info.targetKind === 'unit_unit' ? 'unit_unit'
        : 'unit',
      stage: 'unit',
      targetIds: ids,
    });
  };

  // Проверка чья очередь атаки в hero phase.
  const heroPhaseAttackerIdx: 0 | 1 | null = !isHeroPhase ? null
    : match.phase === PHASE.HERO_FIRST ? (match.firstPlayerIdx as 0 | 1)
    : ((1 - match.firstPlayerIdx) as 0 | 1);
  const isMyHeroPhase = heroPhaseAttackerIdx === myIdx;
  const attackBtnAvailable = isMyHeroPhase && (!me.hero.heroAttackedThisTurn || (me.heroBonusAttackPending || 0) > 0) && !match.matchOver;

  // Подсветка клеток для target-picker'а активки Героя: на стадии 'unit' —
  // юниты-цели; на стадии 'slot' (Кироса) — свои пустые ячейки.
  const heroPickIds = actionPick?.targetIds
    ?? ((heroActivePick?.stage === 'unit' || heroActivePick?.stage === 'unit2')
      ? heroActivePick.targetIds : undefined);
  const heroSlotMode = heroActivePick?.stage === 'slot';

  // Мод вешается на своего юнита: подсвечиваем своих юнитов (на поле +
  // запланированных) как цели; обычное размещение на пустую клетку при
  // выбранном Моде выключено.
  const isModSelected = selectedHandCard?.type === 'mod';
  const placeUnitMode = (isPlacement && !!selectedHandCard && !isModSelected) || heroSlotMode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const modTargetIds: Set<number> | undefined = isModSelected
    ? new Set<number>([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...me.field.flat().filter((u: any) => u && typeof u.instanceId === 'number').map((u: any) => u.instanceId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...[...pendingMap.values()].filter((c: any) => c?.type === 'unit').map((c: any) => c.instanceId),
      ])
    : undefined;
  const myLanePickIds = heroPickIds ?? modTargetIds;

  return (
    <div className={styles.phone}>
      {/* HUD */}
      <div className={styles.hud}>
        <button className={styles.hudBack} onClick={() => navigate('/home')}>← ВЫЙТИ</button>
        <span className={styles.hudTitle}>САНДБОКС</span>
        <button
          className={styles.hudSideBtn}
          onClick={() => {
            setActiveSide((s) => (s === 0 ? 1 : 0));
            setHeroActivePick(null);
            setHeroDiscardPick(null);
            setHeroAttackArmed(false);
          }}
          title="Переключить контролируемую сторону"
        >
          СТОР. {activeSide === 0 ? 'A' : 'B'} ⇄
        </button>
        {/* На широких экранах библиотека всегда открыта (sidepanel), кнопка скрыта. */}
        {!isWideScreen && (
          <button className={styles.hudLibBtn} onClick={() => setLibraryOpen(true)} title="Библиотека всех карт">
            📚 ЛИБ
          </button>
        )}
        <button className={styles.hudLogBtn} onClick={() => setLogOpen(true)} title="Лог боя">
          ≡
        </button>
      </div>

      {/* Враг (top) */}
      <SandboxPbar player={enemy} faction={enemyFaction} isEnemy onHeroTap={onEnemyHeroTap}
                   attackArmed={heroAttackArmed && canHeroAttackHero(match, myIdx)} />

      {/* Field */}
      <div className={styles.field}>
        {/* Тыл врага */}
        <SandboxLane cells={enemy.field[1]} faction={enemyFaction} lineNum={2} isEnemy
                     onTap={(slot) => onCellTap(1, slot, true)}
                     attackArmed={heroAttackArmed}
                     isValidAttack={(u) => canHeroAttackUnit(match, myIdx, u)}
                     actionPickIds={heroPickIds}
                     pendingMap={new Map()} />
        {/* Фронт врага */}
        <SandboxLane cells={enemy.field[0]} faction={enemyFaction} lineNum={1} isEnemy
                     onTap={(slot) => onCellTap(0, slot, true)}
                     attackArmed={heroAttackArmed}
                     isValidAttack={(u) => canHeroAttackUnit(match, myIdx, u)}
                     actionPickIds={heroPickIds}
                     pendingMap={new Map()} />

        {/* War-band */}
        <div className={styles.warBand}>
          <span className={styles.wbLeft}>сторона <b>{activeSide === 0 ? 'A' : 'B'}</b> снизу</span>
          <span className={styles.wbCenter}>Ход {turn} · Ф{phaseNum(match.phase)} · {phase}</span>
          <span className={styles.wbRight}>
            {isHeroPhase ? `атакует ${heroPhaseAttackerIdx === myIdx ? 'СТОР.' + (activeSide===0?'A':'B') : 'против.'}` : ''}
          </span>
        </div>

        {/* Фронт мой */}
        <SandboxLane cells={me.field[0]} faction={myFaction} lineNum={1}
                     onTap={(slot) => onCellTap(0, slot, false)}
                     attackArmed={false}
                     isValidAttack={() => false}
                     actionPickIds={myLanePickIds}
                     pendingMap={pendingMap}
                     placeMode={placeUnitMode} />
        {/* Тыл мой */}
        <SandboxLane cells={me.field[1]} faction={myFaction} lineNum={2}
                     onTap={(slot) => onCellTap(1, slot, false)}
                     attackArmed={false}
                     isValidAttack={() => false}
                     actionPickIds={myLanePickIds}
                     pendingMap={pendingMap}
                     placeMode={placeUnitMode} />
      </div>

      {/* Я (bottom) */}
      <SandboxPbar
        player={me} faction={myFaction} isEnemy={false}
        onHeroTap={() => {
          const heroData = me.hero;
          const data: CardData = {
            id: heroData.id, name_ru: heroData.name_ru,
            faction: heroData.faction, type: 'unit',
            rarity: heroData.rarity || 'special',
            atk: heroData.base?.atk ?? heroData.atk ?? 0,
            armor: heroData.base?.armor ?? heroData.armor ?? 0,
            hp: heroData.base?.hp ?? heroData.hp ?? 0,
            keywords: heroData.keywords || [],
            exclusive_hero: null,
            ability_text: heroData.ability_text || {},
            flavor_ru: heroData.flavor_ru || null,
          };
          setDetailCard(data);
        }}
        attackBtn={attackBtnAvailable ? {
          armed: heroAttackArmed,
          onClick: () => setHeroAttackArmed((v) => !v)
        } : undefined}
        activeBtn={{
          available: getHeroActiveInfo(match, myIdx).available,
          armed: !!heroActivePick || !!heroDiscardPick,
          onClick: () => {
            if (heroActivePick || heroDiscardPick) {
              setHeroActivePick(null);
              setHeroDiscardPick(null);
              return;
            }
            onHeroActiveClick();
          },
        }}
      />

      {/* Рука + actions */}
      <div className={styles.handRow}>
        <div className={styles.hand}>
          {me.hand.length === 0 && (
            <div className={styles.handEmpty}>пусто — добавь через 📚 ЛИБ</div>
          )}
          {me.hand.map((card: any) => {
            const isPending = (match.pendingPlacements?.[myIdx] || []).some(
              (p: any) => p.cardInstanceId === card.instanceId
            ) || pendingActionId === card.instanceId;
            const isSelected = selectedHandCard?.instanceId === card.instanceId;
            const data = getCardData(card.id);
            return (
              <div
                key={card.instanceId}
                className={`${styles.handCard} ${isPending ? styles.handCardPending : ''} ${isSelected ? styles.handCardSelected : ''}`}
                onClick={() => onHandCardSelect(card)}
              >
                <div className={styles.handCardArt}>
                  <img
                    src={`/img/cards/${card.id}.jpg`}
                    alt=""
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
                <div className={styles.handCardName}>{card.name_ru || data?.name_ru || card.id}</div>
                <div className={styles.handCardType}>
                  {card.type === 'unit' && (
                    <>
                      <span style={{ color: '#FF6060' }}>{card.base?.atk ?? data?.atk ?? 0}</span>
                      {' / '}
                      <span style={{ color: 'var(--neon-cy)' }}>{card.base?.armor ?? data?.armor ?? 0}</span>
                      {' / '}
                      <span style={{ color: 'var(--neon-li)' }}>{card.base?.hp ?? data?.hp ?? 0}</span>
                    </>
                  )}
                  {card.type === 'action' && <span style={{ color: 'var(--neon-mg)' }}>ДЕЙСТВИЕ</span>}
                  {card.type === 'mod' && <span style={{ color: 'var(--neon-mg)' }}>МОД</span>}
                </div>
                {isPending && <div className={styles.handCardCheck}>✓</div>}
                {/* Кнопка ✕ убрать из руки */}
                <button
                  className={styles.handCardRm}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeCardFromHand(match, myIdx, card.instanceId);
                    if (isPending) cancelPendingPlacement(match, myIdx, card.instanceId);
                    if (pendingActionId === card.instanceId) clearPlayerPendingAction(match, myIdx);
                    refresh();
                  }}
                  title="Убрать из руки"
                >✕</button>
              </div>
            );
          })}
        </div>
        <div className={styles.endTurnCol}>
          {isPlacement && (
            <button
              className={styles.endTurnBtn}
              onClick={onEndTurn}
              disabled={isProcessing}
              title="Закончить Ф2 → запустить Ф3 (с анимацией)"
            >▶ ХОД</button>
          )}
          {isHeroPhase && (
            <button
              className={styles.passBtn}
              onClick={onPassHero}
              disabled={isProcessing}
              title="Пропустить атаку Героем в этой фазе"
            >⏭ ПАСС</button>
          )}
        </div>
      </div>

      {/* Подсказка selected card */}
      {selectedHandCard && (
        <div className={styles.selectedHint}>
          выбрано: <b>{selectedHandCard.name_ru}</b> — {selectedHandCard.type === 'mod'
            ? 'тапни своего юнита (Мод вешается на юнит)'
            : 'тап на пустую клетку для размещения'}
          <button className={styles.selectedHintCancel} onClick={() => setSelectedHandCard(null)}>✕</button>
        </div>
      )}

      {/* Action pick banner */}
      {actionPick && (
        <div className={styles.pickBanner}>
          <span>выбери цель для <b>{actionPick.card.name_ru}</b> ({actionPick.targetIds.size})</span>
          <button onClick={() => setActionPick(null)}>✕ отмена</button>
        </div>
      )}
      {/* САНД.2: move destination banner (Перегруппировка/Передислокация) */}
      {actionMovePick && (
        <div className={styles.pickBanner}>
          <span><b>{actionMovePick.card.name_ru}</b>: тапни СВОБОДНУЮ клетку своей стороны — куда переместить <b>{actionMovePick.target?.name_ru || 'юнит'}</b></span>
          <button onClick={() => setActionMovePick(null)}>✕ отмена</button>
        </div>
      )}

      {/* Hero-active pick banner */}
      {heroActivePick && (
        <div className={styles.pickBanner}>
          <span>
            {heroActivePick.stage === 'slot'
              ? 'активка Героя: тапни свою пустую ячейку'
              : heroActivePick.stage === 'unit2'
                ? 'активка Героя: выбери юнит-приёмник Мода'
                : heroActivePick.kind === 'unit_slot'
                  ? 'активка Героя: выбери вражеский юнит'
                  : heroActivePick.kind === 'unit_unit'
                    ? 'активка Героя: выбери юнит-источник Мода'
                    : `активка Героя: выбери цель (${heroActivePick.targetIds.size})`}
          </span>
          <button onClick={() => setHeroActivePick(null)}>✕ отмена</button>
        </div>
      )}

      {/* Hero-active result toast */}
      {heroActiveMsg && (
        <div className={`${styles.heroActiveToast} ${heroActiveMsg.ok ? styles.heroActiveToastOk : styles.heroActiveToastErr}`}>
          {heroActiveMsg.text}
        </div>
      )}

      {/* Library:
          • ≥1500px (ultra-wide) — две библиотеки (A слева, B справа);
          • 1100-1499px — одна правая (активной стороны);
          • <1100px — модалка по 📚-кнопке. */}
      {isUltraWide ? (
        <>
          <LibraryModal
            sideIdx={0}
            placement="left"
            defaultFaction={(match.players[0]?.hero?.faction as Faction) || 'deus'}
            onAdd={(tpl) => onAddCardToHand(0, tpl)}
          />
          <LibraryModal
            sideIdx={1}
            placement="right"
            defaultFaction={(match.players[1]?.hero?.faction as Faction) || 'syntetics'}
            onAdd={(tpl) => onAddCardToHand(1, tpl)}
          />
        </>
      ) : isWideScreen ? (
        <LibraryModal
          sideIdx={activeSide}
          placement="right"
          defaultFaction={(match.players[activeSide]?.hero?.faction as Faction) || 'deus'}
          onAdd={(tpl) => onAddCardToHand(activeSide, tpl)}
        />
      ) : libraryOpen ? (
        <LibraryModal
          sideIdx={activeSide}
          placement="modal"
          defaultFaction={(match.players[activeSide]?.hero?.faction as Faction) || 'deus'}
          onClose={() => setLibraryOpen(false)}
          onAdd={(tpl) => onAddCardToHand(activeSide, tpl)}
        />
      ) : null}

      {/* Card detail */}
      {detailCard && (
        <CardDetailModalSandbox
          card={detailCard}
          isAction={detailCard.type === 'action'}
          onClose={() => setDetailCard(null)}
          onPlay={() => {
            const hc = me.hand.find((c: any) => c.id === detailCard.id);
            if (hc) onPlayAction(hc);
          }}
        />
      )}

      {/* Log */}
      {logOpen && (
        <LogPanel
          entries={match.log?.entries || []}
          onClose={() => setLogOpen(false)}
        />
      )}

      {/* Hero-active: выбор юнита из сброса (Лиза) */}
      {heroDiscardPick && (
        <HeroDiscardModal
          choices={heroDiscardPick}
          onClose={() => setHeroDiscardPick(null)}
          onPick={(c) => {
            const r = useHeroActive(match, myIdx, { instanceId: c.instanceId, ownerIdx: c.ownerIdx });
            setHeroDiscardPick(null);
            showHeroActiveResult(r);
            if (r.ok) refresh();
          }}
        />
      )}

      <AnimationLayer animator={animatorRef.current} />

      {match.matchOver && (
        <div className={styles.endOverlay}>
          <div className={styles.endBox}>
            <div className={styles.endTitle}>МАТЧ ОКОНЧЕН</div>
            <div className={styles.endSub}>
              Победил: {match.winnerIdx === 'draw' ? 'НИЧЬЯ'
                       : match.winnerIdx === 0 ? 'Сторона A'
                       : 'Сторона B'}
            </div>
            <button
              className={styles.endBtn}
              onClick={() => {
                setMatch(null);
                setActiveSide(0);
                setHeroAttackArmed(false);
              }}
            >⟳ НОВЫЙ ТЕСТ</button>
          </div>
        </div>
      )}
    </div>
  );
}

function phaseNum(p: string): string {
  switch (p) {
    case PHASE.DRAW: return '1';
    case PHASE.PLACEMENT: return '2';
    case PHASE.REVEAL: return '3';
    case PHASE.HERO_FIRST: return '4';
    case PHASE.HERO_SECOND: return '5';
    case PHASE.END_TURN: return '6';
    default: return '·';
  }
}

// Человекочитаемая причина отказа в размещении (canAddPlacement.reason).
function denyReason(reason?: string): string {
  switch (reason) {
    case 'wrong_faction': return 'Моды играет только Синтетикс';
    case 'budget_exhausted': return 'нет свободных слотов размещения в этом ходу';
    case 'one_mod_per_turn': return 'не больше 1 Мода за ход';
    case 'mod_requires_main': return 'сначала сыграй юнит или Действие в этом ходу';
    case 'one_unit_or_action_per_turn': return 'у Синтетикс — 1 юнит/Действие за ход';
    case 'wrong_card_type': return 'эту карту так не разместить';
    default: return reason || 'недоступно';
  }
}

// ────────── Pbar ──────────
function SandboxPbar({
  player, faction, isEnemy, onHeroTap, attackBtn, attackArmed,
  activeBtn
}: {
  player: any; faction: Faction; isEnemy: boolean;
  onHeroTap: () => void;
  attackBtn?: { armed: boolean; onClick: () => void };
  attackArmed?: boolean;
  // Кнопка активки Героя (только моя сторона). available — активка
  // применима сейчас; armed — идёт выбор цели (клик = отмена).
  activeBtn?: { available: boolean; armed: boolean; onClick: () => void };
}) {
  const hero = player.hero;
  const atk = hero.current?.atk ?? hero.base?.atk ?? 0;
  const armor = hero.current?.armor ?? hero.base?.armor ?? 0;
  const hp = hero.current?.hp ?? hero.base?.hp ?? 0;
  const fShort = factionShort(faction);
  const deckCount = player.deck?.length ?? 0;
  const discardCount = player.discard?.length ?? 0;
  const handCount = player.hand?.length ?? 0;
  const heroName = hero.name_ru || hero.id;

  return (
    <div className={`${styles.pbar} ${isEnemy ? styles.pbarEnemy : styles.pbarMe} ${attackArmed ? styles.pbarAttackArmed : ''}`}>
      <button className={`${styles.pbarAv} ${styles[`av-${fShort}`]}`} onClick={onHeroTap}>
        <HeroAvatar id={hero.id} size={36} faction={faction} shape="square" />
      </button>
      <div className={styles.pbarInfo}>
        <div className={styles.pbarName}>{heroName}</div>
        <div className={styles.pbarStats}>
          <span style={{ color: 'var(--neon-am)' }}>⚔ {atk}</span>
          <span style={{ color: 'var(--neon-cy)' }}>◇ {armor}</span>
          <span style={{ color: 'var(--neon-li)' }}>♥ {hp}</span>
          {faction === 'deus' && player.influence != null && (
            <span style={{ color: 'var(--deus)' }}>◆ {player.influence}</span>
          )}
          <span style={{ color: 'var(--t-mute)' }}>✋{handCount} □{deckCount} ⊟{discardCount}</span>
        </div>
      </div>
      {activeBtn && (
        <button
          className={`${styles.pbarActiveBtn} ${activeBtn.armed ? styles.pbarActiveBtnArmed : activeBtn.available ? styles.pbarActiveBtnOk : ''}`}
          onClick={activeBtn.onClick}
          title="Активка Героя (Ф2)"
        >
          {activeBtn.armed ? '✕ ОТМЕНА' : '◆ АКТИВКА'}
        </button>
      )}
      {attackBtn && (
        <button
          className={`${styles.pbarAtkBtn} ${attackBtn.armed ? styles.pbarAtkBtnArmed : ''}`}
          onClick={attackBtn.onClick}
        >
          {attackBtn.armed ? '✕' : '⚔'}
        </button>
      )}
    </div>
  );
}

// ────────── Lane (4 cells) ──────────
function SandboxLane({
  cells, faction, lineNum, isEnemy, onTap, attackArmed, isValidAttack, actionPickIds, pendingMap, placeMode
}: {
  cells: any[]; faction: Faction; lineNum: 1 | 2; isEnemy?: boolean;
  onTap: (slot: number) => void;
  attackArmed: boolean;
  isValidAttack: (u: any) => boolean;
  actionPickIds?: Set<number>;
  pendingMap: Map<string, any>;
  placeMode?: boolean;
}) {
  const lineIdx = (lineNum === 1 ? 0 : 1) as 0 | 1;
  return (
    <div className={styles.laneRow}>
      <div className={`${styles.laneTag} ${isEnemy ? styles.laneTagEnemy : styles.laneTagMe}`}>
        {lineNum}Л
      </div>
      <div className={styles.lane}>
        {cells.map((unit, slot) => {
          const key = `${lineIdx}-${slot}`;
          const pending = pendingMap.get(key);
          const isEmpty = !unit && !pending;
          const isAttackTarget = attackArmed && unit && isValidAttack(unit);
          const isPickTarget = !!actionPickIds && (
            (!!unit && actionPickIds.has(unit.instanceId)) ||
            (!!pending && actionPickIds.has(pending.instanceId))
          );
          const isPlaceTarget = placeMode === true && isEmpty;
          return (
            <div
              key={slot}
              className={`${styles.cell} ${isEmpty ? styles.cellEmpty : ''}
                ${isAttackTarget ? styles.cellAtkTarget : ''}
                ${isPickTarget ? styles.cellPickTarget : ''}
                ${isPlaceTarget ? styles.cellPlaceTarget : ''}
                ${pending ? styles.cellPending : ''}`}
              onClick={() => onTap(slot)}
            >
              {pending && <UnitMini unit={pending} faction={faction} />}
              {!pending && unit && <UnitMini unit={unit} faction={faction} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────── Library Modal ──────────
function LibraryModal({
  sideIdx, defaultFaction, placement, onClose, onAdd
}: {
  sideIdx: 0 | 1;
  defaultFaction: Faction;
  placement: 'left' | 'right' | 'modal';
  onClose?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAdd: (tpl: any) => void;
}) {
  const [filterFaction, setFilterFaction] = useState<Faction>(defaultFaction);
  const [filterType, setFilterType] = useState<'all' | 'unit' | 'action' | 'mod'>('all');
  const [filterRarity, setFilterRarity] = useState<
    'all' | 'common' | 'rare' | 'epic' | 'legendary' | 'special'
  >('all');
  const [search, setSearch] = useState('');

  const isSidePanel = placement !== 'modal';
  const sideLabel = sideIdx === 0 ? 'A' : 'B';

  const cards = CARDS_BY_FACTION[filterFaction] || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filtered = cards.filter((c: any) => {
    if (filterType !== 'all' && c.type !== filterType) return false;
    if (filterRarity !== 'all' && c.rarity !== filterRarity) return false;
    if (search && !String(c.name_ru || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  // Сортировка: rarity → name.
  const order: Record<string, number> = { legendary: 0, epic: 1, rare: 2, common: 3, special: 4 };
  const sorted = [...filtered].sort((a, b) => {
    const oa = order[a.rarity] ?? 9, ob = order[b.rarity] ?? 9;
    if (oa !== ob) return oa - ob;
    return String(a.name_ru).localeCompare(String(b.name_ru));
  });

  const overlayCls = `${styles.libOverlay} ${placement === 'left' ? styles.libOverlayLeft : ''}`.trim();

  return (
    <div
      className={overlayCls}
      onClick={isSidePanel ? undefined : onClose}
    >
      <div className={styles.libBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.libHeader}>
          <div className={styles.libTitle}>БИБЛИОТЕКА {sideLabel}</div>
          {!isSidePanel && <button className={styles.libClose} onClick={onClose}>×</button>}
        </div>
        <div className={styles.libFilters}>
          <select value={filterFaction} onChange={(e) => setFilterFaction(e.target.value as Faction)}>
            {ALL_FACTIONS.map(f => <option key={f} value={f}>{FACTION_LABEL[f]}</option>)}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value as any)}>
            <option value="all">все типы</option>
            <option value="unit">юниты</option>
            <option value="action">действия</option>
            <option value="mod">моды</option>
          </select>
          <select value={filterRarity} onChange={(e) => setFilterRarity(e.target.value as any)}>
            <option value="all">любая редкость</option>
            <option value="common">обычные</option>
            <option value="rare">редкие</option>
            <option value="epic">эпические</option>
            <option value="legendary">легендарные</option>
            <option value="special">special</option>
          </select>
          <input
            type="text"
            placeholder="поиск по имени…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.libGrid}>
          {sorted.map((c: any) => {
            const cardData = getCardData(c.id) || (c as any);
            return (
              <div
                key={c.id}
                className={styles.libCardWrap}
                onClick={() => onAdd(c)}
                title={`+ в руку «${sideLabel}»: ${c.name_ru}`}
              >
                <GameCard card={cardData as any} variant="full" />
                <button
                  className={styles.libCardAddBtn}
                  onClick={(e) => { e.stopPropagation(); onAdd(c); }}
                >
                  + В РУКУ
                </button>
              </div>
            );
          })}
          {sorted.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--t-mute)' }}>
              — ничего не найдено —
            </div>
          )}
        </div>
        <div className={styles.libHint}>Тап на карту = добавить копию в руку стороны {sideLabel}.</div>
      </div>
    </div>
  );
}

function rarityShort(r: string): string {
  return ({ legendary: 'LEG', epic: 'EPIC', rare: 'RARE', common: 'COM', special: 'SPC' } as any)[r] || r;
}

// ────────── CardDetail (упрощённый) ──────────
function CardDetailModalSandbox({
  card, isAction, onClose, onPlay
}: {
  card: CardData;
  isAction: boolean;
  onClose: () => void;
  onPlay: () => void;
}) {
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.cdmBox} onClick={(e) => e.stopPropagation()}>
        <button className={styles.cdmClose} onClick={onClose}>×</button>
        <div style={{ width: 320, margin: '0 auto' }}>
          <GameCard card={card} variant="full" />
        </div>
        {isAction && (
          <button className={styles.cdmPlay} onClick={onPlay}>
            ► СЫГРАТЬ
          </button>
        )}
      </div>
    </div>
  );
}

// ────────── Log Panel ──────────
function LogPanel({ entries, onClose }: { entries: string[]; onClose: () => void }) {
  const reversed = [...entries].reverse().slice(0, 200);
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.logBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.logHeader}>
          <div className={styles.logTitle}>ЛОГ САНДБОКСА ({entries.length})</div>
          <button
            className={styles.libClose}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(entries.join('\n'));
                alert('Скопировано');
              } catch { alert('Не удалось'); }
            }}
          >📋</button>
          <button className={styles.cdmClose} onClick={onClose}>×</button>
        </div>
        <div className={styles.logList}>
          {reversed.map((l, i) => (
            <div key={i} className={styles.logLine}>{l}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────── Hero-active: выбор юнита из сброса (Лиза) ──────────
function HeroDiscardModal({ choices, onPick, onClose }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  choices: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPick: (c: any) => void;
  onClose: () => void;
}) {
  return (
    <div className={styles.cdmOverlay} onClick={onClose}>
      <div className={styles.discardBox} onClick={(e) => e.stopPropagation()}>
        <div className={styles.discardHeader}>
          <div className={styles.discardTitle}>ВОСКРЕСИТЬ ЮНИТА ИЗ СБРОСА</div>
          <button className={styles.libClose} onClick={onClose}>×</button>
        </div>
        <div className={styles.discardList}>
          {choices.map((c: any, i: number) => (
            <button
              key={c.instanceId ?? i}
              className={styles.discardItem}
              onClick={() => onPick(c)}
            >
              <span className={styles.discardName}>{c.name_ru || c.id}</span>
              <span className={c.isEnemy ? styles.discardEnemy : styles.discardMine}>
                {c.isEnemy ? 'сброс врага' : 'свой сброс'}
              </span>
            </button>
          ))}
          {choices.length === 0 && (
            <div style={{ textAlign: 'center', padding: 16, color: 'var(--t-mute)' }}>
              — сбросы пусты —
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
